/**
 * APIClient - TypeScript wrapper for legacy APIClient
 */
import { userAuth } from './firebase-config';

// Import legacy APIClient
// @ts-ignore
import { APIClient as LegacyAPIClient } from '../legacy/APIClient.js';

// Type definitions
export interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface Entry {
  entryId: string;
  userId: string;
  url: string;
  createdAt: string;
  updatedAt: string;
  liveRevisionNumber?: string;
  liveScriptId?: string;
  liveAudioId?: string;
  products?: Product[];
  queues?: Queue[];
}

export interface Product {
  id: number;
  entry_id: string;
  user_id: string;
  url: string;
  title: string;
  price: string;
  description: string;
  screenshot_path: string;
  extracted_data: any;
  stage: string;
  created_at: string;
  updated_at: string;
}

export interface Script {
  id: number | string;  // Can be either number or UUID string
  entry_id: string;
  user_id: string;
  script_content: string;
  script_file_path: string;
  script_settings: ScriptSettings;
  created_at: string;
  updated_at: string;
  revision_number?: string;
  revision_name?: string;
}

export interface ScriptSettings {
  speaker1: { name: string; gender: 'male' | 'female' | string; voiceName?: string; voice?: string };
  speaker2: { name: string; gender: 'male' | 'female' | string; voiceName?: string; voice?: string };
  scriptLength: number;
  exchangeCount: number;
  language: 'japanese' | 'english';
  styleFormality: number;
  contentBalance: number;
  relationship: number;
  tension: number;
  specialNotes?: string;
}

export interface AudioSettings extends ScriptSettings {
  speaker1Voice?: string;
  speaker2Voice?: string;
}

export interface Audio {
  id: string;
  entry_id: string;
  user_id: string;
  script_id: string;
  audio_file_path: string;
  generation_settings: any;
  created_at: string;
  duration?: number;
  waveform_data?: number[];
  revision_number?: string;
  revision_name?: string;
  is_live_audio?: boolean;
}

export interface Queue {
  id: number;
  queueId: string;
  entryId: string;
  userId: string;
  queueType: 'analyze_product' | 'generate_script' | 'generate_audio';
  status: 'running' | 'completed' | 'error' | 'queued';
  startTime: string | null;
  endTime: string | null;
  errorMessage: string | null;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

export interface Revision {
  id?: number;
  revision_number: string;
  revision_name: string | null;
  created_at: string;
  script?: {
    id: string;
    script_content: string;
    script_file_path: string | null;
    script_settings: ScriptSettings;
    created_at: string;
  };
  audio?: {
    id: string;
    audio_file_path: string;
    generation_settings: any;
    waveform_data: string;
    total_duration: string;
    created_at: string;
  };
  is_live: boolean;
  // For compatibility with our architecture
  script_id?: number | string;
  audio_id?: number | string;
}

export interface Credits {
  balance: number;
  totalEarned: number;
  totalSpent: number;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  role: 'user' | 'admin';
  role_type?: 'user' | 'service_admin' | 'system_admin';
  plan: 'free' | 'paid' | 'poc' | 'cancel';
  credits: number;
  language: 'ja' | 'en';
  createdAt: string;
  accountExpiresAt?: string;
}

// Create an auth manager wrapper that matches legacy interface
const authManager = {
  async getToken() {
    return await userAuth.getToken();
  }
};

class APIClientWrapper extends LegacyAPIClient {
  constructor(baseUrl: string = '/api') {
    super(authManager, baseUrl);
  }

  // Add authenticatedFetch method for direct API calls
  async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    return super.authenticatedFetch(url, options);
  }

  // User initialization
  async initializeUser(plan: string, language: string, registrationDomain: string): Promise<APIResponse<any>> {
    try {
      const response = await this.authenticatedFetch('/api/user/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan, language, registrationDomain }),
      });
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('User initialization failed:', error);
      return {
        success: false,
        error: error.message || 'Failed to initialize user',
      };
    }
  }

  // Get subscription status
  async getSubscriptionStatus(sessionId?: string): Promise<APIResponse<any>> {
    try {
      const url = sessionId 
        ? `/api/payments/subscription-status?session_id=${sessionId}`
        : '/api/payments/subscription-status';
      
      const response = await this.authenticatedFetch(url, {
        method: 'GET',
      });
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Failed to get subscription status:', error);
      return {
        success: false,
        error: error.message || 'Failed to get subscription status',
      };
    }
  }

  // Type-safe method overrides
  async getEntries(): Promise<APIResponse<Entry[]>> {
    return super.getEntries();
  }

  async getEntry(entryId: string): Promise<APIResponse<Entry>> {
    return super.getEntry(entryId);
  }

  async createEntry(url: string): Promise<APIResponse<Entry>> {
    return super.createEntry(url);
  }

  async deleteEntry(entryId: string): Promise<APIResponse<{
    deleted: boolean;
    deletedFiles?: string[];
    deletedRecords?: any;
  }>> {
    return super.deleteEntry(entryId);
  }

  async startProductAnalysis(entryId: string): Promise<APIResponse<{
    queueId: string;
    status: string;
  }>> {
    return super.startProductAnalysis(entryId);
  }

  async generateScript(entryId: string, settings: ScriptSettings): Promise<APIResponse<{
    queueId: string;
    status: string;
  }>> {
    return super.startScriptGeneration(entryId, settings);
  }

  async updateScript(entryId: string, scriptId: number | string, content: string): Promise<APIResponse<Script>> {
    // Convert to string for API call since script IDs can be UUIDs
    return super.updateScript(entryId, scriptId.toString(), content);
  }

  async generateAudio(entryId: string, revisionNumber?: string | null): Promise<APIResponse<{
    queueId: string;
    status: string;
  }>> {
    // @ts-ignore - Legacy API expects different parameter type
    return super.startAudioGeneration(entryId, revisionNumber === undefined ? null : revisionNumber);
  }

  async generateAudioWithSettings(entryId: string, audioSettings: {
    speaker1Voice: string;
    speaker2Voice: string;
  }): Promise<APIResponse<{
    queueId: string;
    status: string;
  }>> {
    const response = await this.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/audios/generate`, {
      method: 'POST',
      body: JSON.stringify(audioSettings)
    });
    
    return await response.json();
  }

  async getAudioStats(entryId: string, audioId: string): Promise<APIResponse<{
    play_count: number;
    avg_play_time: number;
    session_play_times: number[];
  }>> {
    return super.getAudioStats(entryId, audioId);
  }

  async getCredits(): Promise<APIResponse<Credits>> {
    const response = await super.getCreditBalance();
    if (response.success) {
      return {
        success: true,
        data: {
          balance: response.data?.balance || 0,
          totalEarned: response.data?.totalEarned || 0,
          totalSpent: response.data?.totalSpent || 0
        }
      };
    }
    return response;
  }

  async getUserProfile(): Promise<APIResponse<UserProfile>> {
    try {
      const user = userAuth.getCurrentUser();
      if (!user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Get user profile from backend API
      const response = await super.authenticatedFetch(`${this.baseUrl}/user/profile`);
      
      if (!response.ok) {
        return { success: false, error: 'Failed to get user profile' };
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        
        return {
          success: true,
          data: {
            uid: result.data.userId || user.uid,
            email: result.data.email || user.email || '',
            displayName: result.data.displayName || user.displayName || '',
            role: result.data.role || 'user',
            plan: result.data.plan || 'free',
            credits: result.data.credits || 0,
            language: result.data.language || 'ja',
            createdAt: result.data.createdAt || user.metadata.creationTime || '',
            accountExpiresAt: result.data.accountExpiresAt || result.data.account_expires_at
          }
        };
      }

      return { success: false, error: result.error || 'Failed to get user profile' };
    } catch (error) {
      
      // Return basic info as fallback
      const user = userAuth.getCurrentUser();
      if (!user) {
        return { success: false, error: 'Not authenticated' };
      }
      
      return {
        success: true,
        data: {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          role: 'user',
          plan: 'free',
          credits: 0,
          language: 'ja',
          createdAt: user.metadata.creationTime || ''
        }
      };
    }
  }


  // User settings methods
  async updateSettings(settings: { language?: 'ja' | 'en' }): Promise<APIResponse<any>> {
    if (settings.language) {
      return super.updateLanguageSetting(settings.language);
    }
    return { success: true };
  }

  async getPlayerSettings(): Promise<APIResponse<any>> {
    return super.getPlayerSettings();
  }

  async updatePlayerSettings(settings: any): Promise<APIResponse<any>> {
    return super.updatePlayerSettings(settings);
  }

  /**
   * Get queues for an entry
   */
  async getQueues(entryId: string): Promise<APIResponse<Queue[]>> {
    try {
      const response = await super.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/queues`);
      
      if (!response.ok) {
        return {
          success: false,
          error: `Failed to get queues: ${response.status}`
        };
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        return {
          success: true,
          data: data.data
        };
      }
      
      return {
        success: false,
        error: 'Invalid response format'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }


  /**
   * Get revisions for an entry
   */
  async getRevisions(entryId: string): Promise<APIResponse<Revision[]>> {
    try {
      const response = await super.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/revisions`);
      
      if (!response.ok) {
        return {
          success: false,
          error: `Failed to get revisions: ${response.status}`
        };
      }
      
      const data = await response.json();
      
      // Handle the response structure from the API
      if (data.success && data.data) {
        // Map the response to include script_id and audio_id for compatibility
        const revisions = data.data.map((rev: any) => ({
          ...rev,
          script_id: rev.script?.id,
          audio_id: rev.audio?.id
        }));
        
        // Sort revisions from oldest to newest (for left to right display)
        const sortedRevisions = revisions.sort((a: Revision, b: Revision) => {
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });
        
        return {
          success: true,
          data: sortedRevisions
        };
      }
      
      return {
        success: false,
        error: 'Invalid response format'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }


  /**
   * Set live revision for an entry
   */
  async setLiveRevision(entryId: string, revisionNumber: string): Promise<APIResponse<void>> {
    try {
      const response = await super.authenticatedFetch(`${this.baseUrl}/entries/${entryId}/revisions/${revisionNumber}/live`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to set live revision: ${response.status}`);
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get specific script by ID
   * DELETION CANDIDATE: Unused - scripts accessed via revisions API
   */
  async getScript(scriptId: string): Promise<Script | null> {
    try {
      const response = await super.authenticatedFetch(`${this.baseUrl}/scripts/${scriptId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to get script: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get specific audio by ID
   * DELETION CANDIDATE: Unused - audios accessed via revisions API
   */
  async getAudio(audioId: string): Promise<Audio | null> {
    try {
      const response = await super.authenticatedFetch(`${this.baseUrl}/audios/${audioId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to get audio: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      return null;
    }
  }
}

// Export singleton instance
export const apiClient = new APIClientWrapper(process.env.REACT_APP_API_BASE_URL || '/api');

// Export class for testing
export { APIClientWrapper as APIClient };